import React from 'react';
import { MoreVertical, CheckCircle, XCircle, Clock, Ban, Edit, Trash2, Key, Building, User, Briefcase, Star, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import DataTable from '@/components/ui/data-table';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const roleColors = {
  ADMIN: 'bg-teal-600/10 text-teal-600 border-teal-600/20',
  CLIENT: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  TRANSPORTEUR: 'bg-teal-500/10 text-teal-500 border-teal-500/20',
  AGENT: 'bg-amber-600/10 text-amber-700 border-amber-600/20',
};

const roleLabels = {
  ADMIN: 'Admin',
  CLIENT: 'Client',
  TRANSPORTEUR: 'Transporteur',
  AGENT: 'Agent',
};

const statusConfig = {
  ACTIVE: { icon: CheckCircle, color: 'text-green-500', bgColor: 'bg-green-500/10', label: 'Actif' },
  PENDING: { icon: Clock, color: 'text-yellow-500', bgColor: 'bg-yellow-500/10', label: 'En attente' },
  SUSPENDED: { icon: XCircle, color: 'text-amber-500', bgColor: 'bg-amber-500/10', label: 'Suspendu' },
  BANNED: { icon: Ban, color: 'text-red-500', bgColor: 'bg-red-500/10', label: 'Banni' },
};

// Helper pour extraire les infos du profil
const getProfileInfo = (user) => {
  const profile = user.profile_data;
  if (!profile) return null;

  switch (profile.type) {
    case 'client':
      return {
        icon: Building,
        info: profile.company_name || 'Client individuel',
        type: 'Entreprise'
      };
    case 'transporter':
      return {
        icon: Briefcase,
        info: profile.organisation_name || 'Transporteur indépendant',
        type: profile.is_company ? 'Entreprise' : 'Indépendant'
      };
    case 'agent':
      return {
        icon: Star,
        info: profile.role_level || 'Agent',
        type: 'Niveau'
      };
    default:
      return null;
  }
};

const ModernUserTable = ({ 
  users = [], 
  onEdit, 
  onDelete, 
  onActivate, 
  onDeactivate, 
  onResetPassword,
  onViewProfile,
  isLoading = false 
}) => {
  // Configuration des colonnes
  const columns = [
    {
      id: 'name',
      header: 'Nom complet',
      accessorKey: 'full_name',
      cell: ({ row }) => {
        const user = row.original;
        const profileInfo = getProfileInfo(user);
        return (
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-gray-600" />
              </div>
            </div>
            <div className="flex-1">
              <div className="font-medium text-gray-900 dark:text-gray-100">
                {user?.first_name} {user?.last_name}
              </div>
              <div className="text-sm text-gray-500">
                {user?.email}
              </div>
              {/* Affichage des IDs LogiConnect */}
              <div className="mt-1 flex flex-wrap gap-1 items-center">
                {user?.logiconnect_id && (
                  <LogiConnectIdBadge 
                    id={user.logiconnect_id} 
                    showLabel={false}
                    size="sm"
                  />
                )}
                {/* Pour les transporteurs, afficher aussi l'ID du profil transporteur */}
                {user?.profile_data?.type === 'transporter' && user?.profile_data?.logiconnect_id && (
                  <>
                    {user?.logiconnect_id && <span className="text-xs text-muted-foreground">|</span>}
                    <LogiConnectIdBadge 
                      id={user.profile_data.logiconnect_id} 
                      showLabel={false}
                      size="sm"
                      variant="outline"
                    />
                  </>
                )}
                {/* Pour les clients, afficher aussi l'ID du profil client */}
                {user?.profile_data?.type === 'client' && user?.profile_data?.logiconnect_id && user?.profile_data?.logiconnect_id !== user?.logiconnect_id && (
                  <>
                    {user?.logiconnect_id && <span className="text-xs text-muted-foreground">|</span>}
                    <LogiConnectIdBadge 
                      id={user.profile_data.logiconnect_id} 
                      showLabel={false}
                      size="sm"
                      variant="outline"
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      id: 'role',
      header: 'Rôle',
      accessorKey: 'role',
      cell: ({ row }) => {
        const user = row.original;
        const role = user?.role || 'client';
        const roleConfig = roleColors[role];
        const roleLabel = roleLabels[role];
        const profileInfo = getProfileInfo(user);
        
        return (
          <div className="space-y-1">
            <Badge className={roleConfig}>
              {roleLabel}
            </Badge>
            {profileInfo && (
              <div className="flex items-center space-x-1 text-xs text-gray-500">
                <profileInfo.icon className="w-3 h-3" />
                <span>{profileInfo.info}</span>
              </div>
            )}
          </div>
        );
      }
    },
    {
      id: 'status',
      header: 'Statut',
      accessorKey: 'status',
      cell: ({ row }) => {
        const user = row.original;
        const status = user?.status || 'INACTIVE';
        const config = statusConfig[status];
        
        if (!config) {
          return <span className="text-muted-foreground text-sm">N/A</span>;
        }
        
        return (
          <div className="flex items-center space-x-2">
            <config.icon className={`w-4 h-4 ${config.color}`} />
            <Badge className={`${config.bgColor} ${config.color} border-0`}>
              {config.label}
            </Badge>
          </div>
        );
      }
    },
    {
      id: 'phone',
      header: 'Téléphone',
      accessorKey: 'phone_number',
      cell: ({ row }) => {
        const user = row.original;
        return (
          <span className="text-sm text-gray-600">
            {user?.phone_number || 'Non spécifié'}
          </span>
        );
      }
    },
    {
      id: 'date_joined',
      header: 'Date d\'inscription',
      accessorKey: 'date_joined',
      cell: ({ row }) => {
        const user = row.original;
        return (
          <span className="text-sm text-gray-600">
            {user?.date_joined ? new Date(user.date_joined).toLocaleDateString('fr-FR') : 'N/A'}
          </span>
        );
      }
    },
    {
      id: 'last_login',
      header: 'Dernière connexion',
      accessorKey: 'last_login',
      cell: ({ row }) => {
        const user = row.original;
        return (
          <span className="text-sm text-gray-600">
            {user?.last_login ? new Date(user.last_login).toLocaleDateString('fr-FR') : 'Jamais'}
          </span>
        );
      }
    }
  ];

  // Configuration des actions avec dropdown
  const actions = [
    {
      label: 'Actions',
      icon: MoreVertical,
      variant: 'ghost',
      isDropdown: true,
      dropdownItems: [
        {
          label: 'Voir profil',
          icon: Eye,
          onClick: (user) => onViewProfile(user)
        },
        {
          label: 'Modifier',
          icon: Edit,
          onClick: (user) => onEdit(user)
        },
        {
          label: 'Réinitialiser mot de passe',
          icon: Key,
          onClick: (user) => onResetPassword && onResetPassword(user)
        },
        {
          label: 'Activer',
          icon: CheckCircle,
          onClick: (user) => onActivate && onActivate(user),
          hide: (user) => user.is_active
        },
        {
          label: 'Désactiver',
          icon: XCircle,
          onClick: (user) => onDeactivate && onDeactivate(user),
          hide: (user) => !user.is_active
        },
        {
          label: 'Supprimer',
          icon: Trash2,
          onClick: (user) => onDelete && onDelete(user),
          variant: 'destructive'
        }
      ]
    }
  ];

  // Fonction pour gérer le clic sur une ligne
  const handleRowClick = (user) => {
    onViewProfile(user);
  };

  return (
    <DataTable
      data={users}
      columns={columns}
      actions={actions}
      onRowClick={handleRowClick}
      searchable={false}
      sortable={true}
      pagination={true}
      pageSize={10}
      className="w-full"
    />
  );
};

export default ModernUserTable;
